'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { BoxType } from '../types';

interface BoxTypeCompactRowProps {
  boxType: BoxType;
  onEdit: (item: BoxType) => void;
  onDelete: (item: BoxType) => void;
}

export function BoxTypeCompactRow({
  boxType,
  onEdit,
  onDelete
}: BoxTypeCompactRowProps) {
  const t = useTranslations('boxTypes');

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-medium'>{boxType.name}</span>
          <span className='text-muted-foreground hidden truncate font-mono text-sm sm:inline'>
            {boxType.code}
          </span>
        </div>
      </div>

      <span className='text-muted-foreground hidden text-sm md:inline'>
        {t('maximumCapacity')}:{' '}
        <span className='text-foreground font-medium'>
          {boxType.max_capacity}
        </span>
      </span>

      <span className='text-muted-foreground hidden text-sm md:inline'>
        {boxType.description ?? '—'}
      </span>

      <Badge
        variant={boxType.status === 'active' ? 'default' : 'secondary'}
        className={cn(
          'shrink-0 text-xs',
          boxType.status === 'active'
            ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
            : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
        )}
      >
        {boxType.status === 'active' ? t('statusActive') : t('statusInactive')}
      </Badge>

      <div className='flex shrink-0 items-center gap-1'>
        <PermissionGate permissions={['box_types.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onEdit(boxType)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['box_types.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='text-destructive h-7 w-7'
            onClick={() => onDelete(boxType)}
          >
            <Trash2 className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
